import { siteHost } from '@/lib/customerArea'
import { siteContactMode } from '@/lib/siteContact'
import { filledSocialNetworks, type SocialLinks } from '@/lib/socialLinks'
import { resolveSiteTemplate } from '@/lib/siteTemplates'
import type { Labeled } from '@/lib/tone'
import type { CompanyConfig } from '@/pages/Companies/GetCompany/types/config'

export const SITE_SECTION_IDS = [
  'personalizacao',
  'pagina-inicial',
  'redes-sociais',
  'dominio',
  'seo',
  'chat',
  'blog',
  'sobre',
  'area-do-cliente',
  'anuncie'
] as const

export type SiteSectionId = (typeof SITE_SECTION_IDS)[number]

export const DEFAULT_SITE_SECTION: SiteSectionId = 'personalizacao'

export const SITE_SECTION_LABEL: Record<SiteSectionId, string> = {
  personalizacao: 'Aparência',
  'pagina-inicial': 'Página inicial',
  'redes-sociais': 'Redes sociais',
  dominio: 'Domínios',
  seo: 'SEO',
  chat: 'Atendimento',
  blog: 'Blog',
  sobre: 'Página Sobre',
  'area-do-cliente': 'Área do cliente',
  anuncie: 'Anuncie conosco'
}

export function siteSectionOf(slug: string | undefined): SiteSectionId | null {
  return (SITE_SECTION_IDS as readonly string[]).includes(slug ?? '') ? (slug as SiteSectionId) : null
}

const toggle = (enabled: boolean, on = 'Ligado', off = 'Desligado'): Labeled =>
  enabled ? { label: on, tone: 'success' } : { label: off, tone: 'neutral' }

export function siteSectionHint(id: SiteSectionId, config: CompanyConfig | null | undefined): Labeled | null {
  if (!config) return null
  switch (id) {
    case 'personalizacao':
      return { label: resolveSiteTemplate(config.theme?.style).name, tone: 'neutral' }
    case 'redes-sociais': {
      const count = filledSocialNetworks(config.socialLinks as SocialLinks | undefined).length
      return count > 0 ? { label: String(count), tone: 'neutral' } : null
    }
    case 'dominio': {
      const count = config.urls?.length ?? 0
      return count > 0 ? { label: String(count), tone: 'neutral' } : { label: 'Nenhum', tone: 'warning' }
    }
    case 'chat': {
      const mode = siteContactMode(config)
      return mode === 'none' ? toggle(false) : { label: mode === 'chat' ? 'Chat' : 'WhatsApp', tone: 'success' }
    }
    case 'blog':
      return toggle(!!config.blog?.enabled)
    case 'sobre':
      return toggle(!!config.about?.enabled, 'Ligada', 'Desligada')
    case 'area-do-cliente':
      return toggle(config.customerPortal?.enabled !== false, 'Ligada', 'Desligada')
    case 'anuncie':
      return toggle(config.listingIntake?.enabled === true)
    default:
      return null
  }
}

export interface MarketplaceAddress {
  protocol?: string
  port?: string
}

const isLocal = (host: string) => host === 'localhost' || host.endsWith('.localhost')

export function siteUrl(config: Pick<CompanyConfig, 'urls'> | null | undefined, env: MarketplaceAddress = {}): string | null {
  const host = siteHost(config)
  if (!host) return null
  const protocol = env.protocol || (isLocal(host) ? 'http' : 'https')
  const port = env.port && isLocal(host) ? `:${env.port}` : ''
  return `${protocol}://${host}${port}/`
}
